// The "first-sync-running" stage (§s03, WS-3 step 3): accounts and balances
// are already listed ("final"); history backfill progress is polled from
// GET /api/jobs/{id}. Plaid backfills page by page -- there is no total, so
// this renders an indeterminate bar plus a real count, never a fabricated
// percentage.
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Progress } from '@moneyskyline/ui';
import { HonestInline } from '@/components/HonestInline';
import { useJob } from '@/api/hooks/useJob';

export function FirstSyncPanel({ jobId, itemId, onDone }: { jobId: string; itemId: string | null; onDone: () => void }) {
  const jobQuery = useJob(jobId);
  const status = jobQuery.data?.status;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (status === 'done' && itemId && jobQuery.data?.params.item_id === itemId) {
      for (const queryKey of [['accounts'], ['plaid', 'items'], ['transactions'], ['sync-runs'], ['spend']]) {
        void queryClient.invalidateQueries({ queryKey });
      }
      onDone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (jobQuery.isLoading) {
    return (
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4" data-testid="first-sync-panel">
        <div className="h-4 w-40 animate-pulse rounded bg-muted" aria-hidden="true" />
      </div>
    );
  }

  if (jobQuery.isError) {
    return <HonestInline kind="api-unavailable" />;
  }

  const job = jobQuery.data;

  if (!job) {
    return null;
  }

  if (job.status === 'failed' || job.status === 'cancelled') {
    return <HonestInline kind="sync-failed" />;
  }

  if (job.status === 'done' && itemId && job.params.item_id === itemId) {
    // The useEffect above already called onDone(); render nothing more here.
    return null;
  }

  if (job.status === 'done') {
    return <HonestInline kind="api-unavailable" />;
  }

  const pagesSynced = typeof job.progress.pages_synced === 'number' ? job.progress.pages_synced : null;
  const added = typeof job.progress.added === 'number' ? job.progress.added : null;

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4" data-testid="first-sync-panel">
      <h2 className="text-title">First sync is running</h2>
      <Progress value={undefined} className="animate-pulse" aria-label="First sync in progress" />
      <p className="text-sm text-muted-foreground">
        Plaid backfills history page by page, so there is no total to show.
        {pagesSynced !== null && ` ${pagesSynced} page${pagesSynced === 1 ? '' : 's'} synced so far`}
        {added !== null && `, ${added} transaction${added === 1 ? '' : 's'} found`}.
      </p>
    </div>
  );
}

export default FirstSyncPanel;
